import React, { useState, useEffect, memo } from 'react';
import PropTypes, { exact } from 'prop-types';
import CUSTOM_EVENT from '../../../util/customEvents';
import Icon from '../Atoms/Icon';
import Loading from '../Core/Loading';

const propTypes = exact({
  tourKey: PropTypes.string.isRequired,
  compareTextLabel: PropTypes.string.isRequired,
});

export const compareItemButtonContainerClass = 'CompareItemButton_container';

const CompareItemButton = memo(({ tourKey, compareTextLabel }) => {
  const [isCompareButtonSelected, setIsCompareButtonSelected] = useState(false);
  const [isCompareButtonLoading, setIsCompareButtonLoading] = useState(false);
  const [isListAtMaxComparisonAmount, setIsListAtMaxComparisonAmount] = useState(false);

  // Listen for custom event in helpers.js / CompareList.jsx when the compareKeyList is updated
  // and respond by handling selected and loading states.
  useEffect(() => {
    const handleCompareKeyListUpdate = (event) => {
      const { compareKeyList, isAtMaxComparisonAmount } = event.detail;
      setIsCompareButtonSelected(compareKeyList.includes(tourKey));
      setIsCompareButtonLoading(false);
      setIsListAtMaxComparisonAmount(isAtMaxComparisonAmount);
    };

    window.addEventListener(CUSTOM_EVENT.COMPARE_KEY_LIST_UPDATED, handleCompareKeyListUpdate);

    return () => {
      window.removeEventListener(CUSTOM_EVENT.COMPARE_KEY_LIST_UPDATED, handleCompareKeyListUpdate);
    };
  }, []);

  // Set all compare item buttons to loading when on is clicked to stop race conditions.
  useEffect(() => {
    const handleCompareButtonClickLoading = () => setIsCompareButtonLoading(true);

    window.addEventListener(CUSTOM_EVENT.COMPARE_KEY_UPDATE, handleCompareButtonClickLoading);

    return () => {
      window.removeEventListener(CUSTOM_EVENT.COMPARE_KEY_UPDATE, handleCompareButtonClickLoading);
    };
  }, []);

  const handleCompareButtonClick = () => {
    // Send event to CompareList with the compare key to update.
    if (!isCompareButtonLoading) {
      window.dispatchEvent(
        new CustomEvent(CUSTOM_EVENT.COMPARE_KEY_UPDATE, { detail: { tourKey } })
      );
    }
  };

  const getButtonLabel = () => {
    if (isCompareButtonLoading) {
      return <Loading />;
    }
    return (
      <>
        <Icon
          id="compare"
          blockName={`CompareItemButton_icon ${
            !isCompareButtonSelected && isListAtMaxComparisonAmount ? 'maxCompareItemsReached' : ''
          }`}
        />
        {compareTextLabel}
      </>
    );
  };

  const getCompareItemButtonClassNames = () =>
    [
      compareItemButtonContainerClass,
      isCompareButtonSelected ? 'selected' : '',
      !isCompareButtonSelected && isListAtMaxComparisonAmount ? 'maxCompareItemsReached' : '',
    ]
      .filter(Boolean)
      .join(' ');

  return (
    <button
      className={getCompareItemButtonClassNames()}
      type="button"
      onClick={handleCompareButtonClick}
      disabled={isCompareButtonLoading}
    >
      {getButtonLabel()}
    </button>
  );
});

CompareItemButton.propTypes = propTypes;

export default CompareItemButton;
